export const dataCollectionPathways = {
  A: {
    id: 'A',
    title: 'Survey Questionnaires',
    icon: 'ClipboardList',
    description: 'Design effective surveys with proper question types, scales, and distribution strategies.',
    steps: {
      A1: {
        id: 'A1',
        title: 'Survey Planning & Objectives',
        introduction: 'Before designing questions, align your survey with research objectives. Clear objectives prevent scope creep and ensure every question has a purpose.',
        tips: [
          {
            title: 'Map objectives to questions',
            content: 'For each research objective, identify 2-3 questions that directly address it. Example: Objective "Understand customer satisfaction" → Questions on product quality, service, value.'
          },
          {
            title: 'Define your population',
            content: 'Specify exactly who should answer (age, role, experience level). Narrow scope = more relevant data (e.g., "Managers with 5+ years experience" vs. "All employees").'
          },
          {
            title: 'Set realistic length',
            content: 'Aim for 5-10 minutes completion time (20-30 questions max). Longer surveys = higher dropout rates (10-15 min = 20% dropout, 20+ min = 35%+).'
          },
          {
            title: 'Choose delivery method early',
            content: 'Online (Qualtrics, Google Forms) vs. paper vs. phone. Online: Best for branching logic, multimedia, auto-validation. Paper: Better for older populations, low digital literacy.'
          },
          {
            title: 'Plan for incentives',
            content: 'Small token = 10-20% higher response rate. Examples: Gift card lottery, charity donation per response, free report of findings.'
          },
          {
            title: 'Pre-test conceptually',
            content: 'Write objectives on paper, then draft questions WITHOUT looking at objectives. Come back 1 hour later and check alignment—fixes 70% of irrelevant questions.'
          },
          {
            title: 'Budget for piloting',
            content: 'Reserve 10% of sample for pilot testing (test 5-10 people before full launch). Reveals confusing wording, technical issues, time overruns.'
          },
          {
            title: 'Consider mobile-first',
            content: '60%+ of online surveys are completed on mobile. Use single-column layout, large tap targets, avoid matrices on mobile.'
          },
          {
            title: 'Plan data analysis upfront',
            content: 'Know your analysis method before finalizing questions. Quantitative analysis needs closed-ended questions; qualitative needs open-ended.'
          },
          {
            title: 'Ethical clearance check',
            content: 'If academic research, submit to IRB/ethics board BEFORE data collection. Include consent statement, data storage plan, anonymization strategy.'
          }
        ]
      },
      A2: {
        id: 'A2',
        title: 'Multiple Choice Questions',
        introduction: 'Multiple choice questions (MCQs) provide structured, easily analyzable data. They work best for factual information, preferences, and behaviors with limited answer options.',
        tips: [
          {
            title: 'Keep options mutually exclusive',
            content: 'Each option should be distinct with no overlap. Bad: "20-30 years" and "25-35 years" (overlap at 25-30). Good: "20-24 years", "25-29 years", "30-34 years".'
          },
          {
            title: 'Make options collectively exhaustive',
            content: 'Cover all possible answers. Always include "Other (please specify)" or "None of the above" as safety net.'
          },
          {
            title: 'Use 4-7 options',
            content: 'Fewer than 4 = forced choice; more than 7 = cognitive overload. Sweet spot: 5 options (like 5-point Likert scale).'
          },
          {
            title: 'Randomize option order',
            content: 'Prevents order bias (tendency to pick first/middle option). Exception: Keep logical sequences (Never → Always, Strongly Disagree → Strongly Agree).'
          },
          {
            title: 'Avoid double-barreled options',
            content: 'One concept per option. Bad: "Quality and price are important". Good: Separate questions for quality and price.'
          },
          {
            title: 'Use "Select all that apply" sparingly',
            content: 'Leads to over-selection bias. Alternative: "Select up to 3 options" (forces prioritization).'
          },
          {
            title: 'Balance positive/negative options',
            content: 'Equal number of favorable/unfavorable choices. Biased: "Excellent, Very Good, Good, Acceptable" (all positive). Balanced: "Very satisfied, Satisfied, Neutral, Dissatisfied, Very dissatisfied".'
          },
          {
            title: 'Include "Don\'t know" or "Not applicable" when appropriate',
            content: 'Prevents random guessing. Use when knowledge/experience may vary (e.g., "Have you used product X?").'
          },
          {
            title: 'Pre-code responses',
            content: 'Assign numeric codes during design, not after data collection. Example: Yes=1, No=2, Don\'t know=9 (speeds up analysis).'
          },
          {
            title: 'Test for primacy/recency effects',
            content: 'First and last options are selected more often. Solution: Rotate option order across respondents if your platform allows.'
          }
        ]
      },
      A3: {
        id: 'A3',
        title: 'Likert Scales (Even vs Odd)',
        introduction: 'Likert scales measure attitudes, opinions, and agreement levels. The choice between even (4, 6-point) and odd (5, 7-point) scales depends on whether you want to allow neutral responses.',
        tips: [
          {
            title: 'Odd scales (5, 7-point) = Allow neutral/undecided',
            content: 'Use when: Respondents may genuinely have no opinion. Example: "Rate your satisfaction with a new feature you haven\'t tried" → needs "Neutral".'
          },
          {
            title: 'Even scales (4, 6-point) = Force choice, no fence-sitting',
            content: 'Use when: You need directional feedback (positive vs. negative). Example: "After using our product, are you satisfied?" → force commitment.'
          },
          {
            title: '5-point is most common',
            content: 'Balances simplicity with granularity. Standard: Strongly Disagree (1) → Strongly Agree (5). Respondents understand intuitively, high completion rates.'
          },
          {
            title: '7-point provides more variance',
            content: 'Better for statistical analysis (more granular data). Use when: Sample size >200, doing regression/correlation analysis. Trade-off: Slightly longer completion time.'
          },
          {
            title: 'Label all points vs. endpoints only',
            content: 'Full labels = clearer, but more space. Full labels: "Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree". Endpoints only: "1 (Strongly Disagree) ... 5 (Strongly Agree)".'
          },
          {
            title: 'Keep direction consistent',
            content: 'All scales should go same direction (low to high). Don\'t mix "Strongly Agree → Strongly Disagree" with "Poor → Excellent" in reverse order.'
          },
          {
            title: 'Use unipolar for frequency',
            content: '"Never, Rarely, Sometimes, Often, Always". Not: "Never ... Neutral ... Always" (frequency has no neutral midpoint).'
          },
          {
            title: 'Avoid extreme labels',
            content: '"Absolutely certain" is stronger than "Strongly agree". Stick to standard labels for comparability across studies.'
          },
          {
            title: 'Middle option ≠ "Don\'t know"',
            content: 'Neutral means indifference, not ignorance. If respondents may not know, add separate "Don\'t know" option outside scale.'
          },
          {
            title: 'Even scales reduce acquiescence bias',
            content: 'Tendency to agree with statements. 4-point forces respondents off automatic "Agree" default.'
          }
        ]
      },
      A4: {
        id: 'A4',
        title: 'Scaling Questions (1-10, Semantic Differential)',
        introduction: 'Scaling questions capture intensity of feelings using numeric or semantic scales. Common formats: 1-10 rating, 0-100 slider, semantic differential (opposite adjectives).',
        tips: [
          {
            title: '0-10 scale = Net Promoter Score (NPS) standard',
            content: '"How likely are you to recommend?" 0-6 = Detractors, 7-8 = Passives, 9-10 = Promoters. Calculate: %Promoters - %Detractors = NPS score (-100 to +100).'
          },
          {
            title: '1-10 feels more intuitive than 0-10',
            content: 'People think in 1-10 (like grades). Use 0-10 only for NPS or when "zero" is meaningful (e.g., "no pain" = 0).'
          },
          {
            title: 'Label endpoints clearly',
            content: '"Not at all important (1) ... Extremely important (10)". Without labels, respondents interpret scales differently.'
          },
          {
            title: 'Sliders (0-100) give illusion of precision',
            content: 'Best for continuous variables. Example: "What % of your time is spent on research?" Warning: False precision—most people think in 10% increments.'
          },
          {
            title: 'Semantic differential uses opposite adjectives',
            content: 'Measures connotative meaning. Format: "Modern [1-2-3-4-5-6-7] Outdated". Best for: Brand perception, product attributes, emotional responses.'
          },
          {
            title: 'Use 7-point for semantic differential',
            content: 'Odd scale allows neutral midpoint. Example: "Friendly [1-2-3-4-5-6-7] Unfriendly" (4 = neither friendly nor unfriendly).'
          },
          {
            title: 'Avoid mixing scales in one section',
            content: 'All 1-10, OR all semantic differential. Switching mid-survey confuses respondents and increases error rates.'
          },
          {
            title: 'Numeric scales encode better',
            content: '1-10 is easier to analyze than semantic differential. Semantic differential requires recoding (which end is positive?).'
          },
          {
            title: 'Visual analog scales (VAS) for pain/emotion',
            content: 'Sliding bar with no numbers shown. Example: Face icons from sad to happy. Best for: Children, low literacy populations, subjective experiences.'
          },
          {
            title: 'Pre-test scale granularity',
            content: 'Do respondents use full range or cluster at extremes? If 90% choose 8-10, scale is too coarse—use 8-15 scale or rephrase question.'
          }
        ]
      },
      A5: {
        id: 'A5',
        title: 'Open-Ended Questions',
        introduction: 'Open-ended questions allow respondents to answer in their own words, providing rich qualitative data. Use strategically—they\'re time-consuming to answer and analyze.',
        tips: [
          {
            title: 'Use open-ended for "why" and "how"',
            content: 'Capture reasoning, not just facts. Example: "Why did you choose this research strategy?" (can\'t get this from MCQ).'
          },
          {
            title: 'Place at the end of survey',
            content: 'Respondents are warmed up, less likely to abandon. Exception: Screening questions ("What is your research topic?") go at start.'
          },
          {
            title: 'Limit to 2-4 open-ended questions',
            content: 'Each adds ~30 seconds, increases dropout. Rule: 1 open-ended per 10 closed-ended questions.'
          },
          {
            title: 'Provide context before asking',
            content: 'Prime respondents with specific focus. Vague: "What do you think about the wizard?" Specific: "What feature of the wizard was most helpful in designing your survey?"'
          },
          {
            title: 'Set character limits strategically',
            content: '500 characters = 2-3 sentences (sweet spot). Too short (<100) = superficial answers; Too long (>1000) = essay burden.'
          },
          {
            title: 'Use text box size as cue',
            content: 'Large box signals long answer expected; small box = brief. Single line = 1-2 words (name, age); Multi-line (3-5 rows) = paragraph.'
          },
          {
            title: 'Make optional when possible',
            content: '"Please share any additional comments (optional)". Required open-ended questions increase abandonment by 15-25%.'
          },
          {
            title: 'Pre-code if possible',
            content: 'If expecting common themes, provide categories + "Other (specify)". Hybrid: "Select main reason [dropdown], then explain: [text box]".'
          },
          {
            title: 'Avoid asking what you can infer',
            content: 'Don\'t ask age in open-ended ("How old are you?"). Use closed-ended for demographics, open-ended for insights.'
          },
          {
            title: 'Plan analysis method upfront',
            content: 'Thematic coding? Word cloud? Manual review? Tools: NVivo, MAXQDA (qualitative), or Python (word frequency analysis).'
          }
        ]
      },
      A6: {
        id: 'A6',
        title: 'Filtering & Skip Logic',
        introduction: 'Skip logic (branching) shows/hides questions based on previous answers. Reduces respondent burden and improves data quality by avoiding irrelevant questions.',
        tips: [
          {
            title: 'Use screening questions early',
            content: '"Do you use our product?" If No → skip to end. Prevents non-users from answering detailed usage questions (garbage data).'
          },
          {
            title: 'Keep logic simple',
            content: 'Maximum 2 levels of nesting (if-then-if). Complex trees confuse respondents and break in survey tools.'
          },
          {
            title: 'Show progress bar carefully',
            content: 'Branching makes "% complete" unpredictable. Solution: "Page 3 of 5-8 pages" OR hide progress bar entirely.'
          },
          {
            title: 'Disqualify gracefully',
            content: '"Thank you. Based on your response, this survey isn\'t relevant to you." Offer incentive anyway (builds goodwill) or redirect to different survey.'
          },
          {
            title: 'Use matrix logic sparingly',
            content: '"If you selected \'Yes\' to ANY item above, answer below..." Respondents forget previous answers—use simple yes/no gates instead.'
          },
          {
            title: 'Test every path',
            content: 'Manually walk through all possible combinations. Example: Yes→Section B→Question 7; No→Section C→Question 10; check both work.'
          },
          {
            title: 'Avoid loop-backs',
            content: 'Don\'t send respondents backward in survey. Bad: Answer Q5 → Skip to Q8 → Based on Q8, return to Q6 (confusing!).'
          },
          {
            title: 'Display logic vs. skip logic',
            content: 'Display = show/hide on same page; Skip = next page. Display logic: Better UX (instant), but reveals survey structure.'
          },
          {
            title: 'Limit branches per question',
            content: 'Max 3 branches per response. Example: "Age: 18-25→Survey A, 26-40→Survey B, 41+→Survey C" (manageable).'
          },
          {
            title: 'Include "catch-all" branch',
            content: 'Always have a default path for unexpected responses. Example: "If none of the above, skip to final question".'
          }
        ]
      },
      A7: {
        id: 'A7',
        title: 'Question Ordering & Flow',
        introduction: 'The sequence of questions affects response quality, completion rates, and bias. Strategic ordering keeps respondents engaged and minimizes priming effects.',
        tips: [
          {
            title: 'Start with easy, engaging questions',
            content: 'Build confidence and momentum. Good opening: "What is your research field?" (simple, relevant). Bad opening: "Rate the epistemological rigor of your ontology" (intimidating!).'
          },
          {
            title: 'Use funnel technique',
            content: 'General → Specific → Very specific. Example: "Research experience (years)" → "Primary method" → "Specific technique used".'
          },
          {
            title: 'Group related questions',
            content: 'All demographics together, all satisfaction questions together. Exception: Spread out similar Likert scales to prevent "straight-lining".'
          },
          {
            title: 'Place demographics at the end',
            content: 'Age, income, education feel intrusive at start. Exception: If used for screening ("Are you 18+?"), place at beginning.'
          },
          {
            title: 'Avoid priming effects',
            content: 'Earlier questions bias later answers. Bad: "Are you satisfied with product quality?" then "Overall satisfaction?" Good: Ask overall satisfaction first, then drill into specifics.'
          },
          {
            title: 'Use transition statements',
            content: '"Now we\'ll ask about your experience with the wizard..." Signals topic change, gives mental break, improves engagement.'
          },
          {
            title: 'Alternate question types',
            content: 'Don\'t put 10 Likert scales in a row (boring!). Pattern: MCQ → Likert → Open-ended → MCQ → Rating scale (variety maintains attention).'
          },
          {
            title: 'Place sensitive questions strategically',
            content: 'After rapport is built (mid-late survey). Example: Income, political views, health info—surround with related but easier questions.'
          },
          {
            title: 'End with open-ended "catch-all"',
            content: '"Any other comments or suggestions?" Captures issues you didn\'t anticipate, signals survey is ending.'
          },
          {
            title: 'Include progress indicators',
            content: '"Section 2 of 4: Survey Design Questions". Reduces abandonment by 10-15% (people endure if they see endpoint).'
          }
        ]
      },
      A8: {
        id: 'A8',
        title: 'Survey Distribution & Piloting',
        introduction: 'Even a perfectly designed survey fails without proper distribution and testing. Pilot testing reveals flaws, and strategic distribution maximizes response rates.',
        tips: [
          {
            title: 'Pilot with 5-10 people from target population',
            content: 'Not colleagues, but actual respondents. Ask: "Were any questions confusing?" "How long did it take?" "Did skip logic work?"'
          },
          {
            title: 'Conduct cognitive interviews during pilot',
            content: 'Ask respondents to "think aloud" as they answer. Reveals misinterpretations (e.g., "By \'usage,\' do you mean daily or ever?").'
          },
          {
            title: 'Check completion time',
            content: 'If >10 minutes, cut 20-30% of questions. Completion time inversely correlates with quality (rushed answers after minute 8-10).'
          },
          {
            title: 'Test on multiple devices',
            content: 'Desktop, mobile, tablet (60% of responses are mobile now). Check: Are buttons big enough? Does matrix display properly?'
          },
          {
            title: 'Send personalized invitations',
            content: '"Dear [Name], we value YOUR feedback..." vs. mass email. Personalization increases response rate by 10-15%.'
          },
          {
            title: 'Optimize send time',
            content: 'Tuesday-Thursday, 10am-2pm = highest open rates. Avoid Mondays (overwhelmed), Fridays (checked out), evenings (personal time).'
          },
          {
            title: 'Send 2-3 reminders',
            content: 'Day 3, Day 7, Day 10 (final call). Each reminder captures 10-15% more responses (but diminishing returns after 3).'
          },
          {
            title: 'Offer incentives strategically',
            content: 'Immediate small reward (coffee gift card) > lottery for big prize. $5 guaranteed > chance at $100 (certainty effect).'
          },
          {
            title: 'Use multiple channels',
            content: 'Email + social media + in-person (QR code on flyer). Different channels reach different demographics.'
          },
          {
            title: 'Monitor response rate in real-time',
            content: 'If <20% after 48 hours, revise distribution strategy. Possible fixes: Shorten survey, improve subject line, add incentive, expand sample.'
          }
        ]
      }
    }
  },
  B: {
    id: 'B',
    title: 'Interviews',
    icon: 'Users',
    description: 'Conduct effective interviews from structured to unstructured formats with proper technique.',
    steps: {
      B1: {
        id: 'B1',
        title: 'Interview Type Selection',
        introduction: 'Choose interview type based on research objectives and flexibility needed. Structured = standardized data, Unstructured = exploratory depth, Semi-structured = balanced.',
        tips: [
          {
            title: 'Structured interviews = Same questions, same order, every time',
            content: 'Use when: Comparing across respondents, need quantifiable data, limited interview experience. Example: Exit interviews (HR), customer satisfaction calls.'
          },
          {
            title: 'Semi-structured = Prepared questions + flexibility to probe',
            content: 'Use when: Exploratory research, need depth but also comparability, PhD interviews. Example: "Tell me about your research process" + follow-ups based on answers.'
          },
          {
            title: 'Unstructured = Open conversation with broad topic',
            content: 'Use when: Generating theory, early-stage research, ethnography, life histories. Example: "Tell me your story of becoming a researcher" (no predetermined questions).'
          },
          {
            title: 'Structured interviews are fastest',
            content: '20-30 minutes (fixed questions). Semi-structured: 45-60 minutes; Unstructured: 60-120 minutes.'
          },
          {
            title: 'Reliability vs. validity trade-off',
            content: 'Structured = high reliability (repeatable), low validity (may miss nuance). Unstructured = low reliability (varies by interviewer), high validity (rich detail).'
          },
          {
            title: 'Match to research paradigm',
            content: 'Positivist → Structured; Interpretivist → Unstructured. Semi-structured works for pragmatist/mixed methods.'
          },
          {
            title: 'Interviewer skill requirements',
            content: 'Structured = Low (read script); Unstructured = High (active listening, probing). If novice researcher, start with structured or semi-structured.'
          },
          {
            title: 'Data analysis differs',
            content: 'Structured = code like survey (frequency counts). Semi-structured/Unstructured = thematic analysis, NVivo coding (time-intensive).'
          },
          {
            title: 'Sample size implications',
            content: 'Structured = can handle 50-100 interviews. Unstructured = 10-20 interviews (diminishing returns after saturation).'
          },
          {
            title: 'Hybrid approach',
            content: 'Start unstructured (5 interviews) to generate themes → Use themes to build semi-structured guide. Combines exploratory depth with systematic comparison.'
          }
        ]
      },
      B2: {
        id: 'B2',
        title: 'Question Design by Interview Type',
        introduction: 'Question phrasing differs by interview type. Structured uses closed-ended, unstructured uses open-ended prompts, semi-structured mixes both.',
        tips: [
          {
            title: 'Structured: Write word-for-word scripts',
            content: 'Interviewer reads verbatim, no paraphrasing. Include: "Now I\'m going to ask you about..." (transitions).'
          },
          {
            title: 'Structured: Use closed-ended questions',
            content: '"Yes/No", "Select from list", "Rate 1-10". Example: "On a scale of 1-10, how satisfied are you with the wizard?"'
          },
          {
            title: 'Semi-structured: Prepare topic guide, not script',
            content: 'List themes/topics + sample questions. Example topic: "Data collection challenges" → Sample Qs: "What difficulties did you face?" "How did you overcome them?"'
          },
          {
            title: 'Semi-structured: Start with grand tour question',
            content: 'Broad opening that lets respondent set direction. "Can you walk me through how you designed your survey?"'
          },
          {
            title: 'Semi-structured: Use open-ended questions',
            content: '"How...", "Why...", "Can you tell me about...". Avoid: "Did you find it difficult?" (Yes/No). Better: "What difficulties did you encounter?"'
          },
          {
            title: 'Semi-structured: Prepare 5-7 main questions + probes',
            content: 'Each main question has 3-5 follow-up probes. Main: "How did you select your methodology?" Probes: "Why that one?", "What alternatives did you consider?"'
          },
          {
            title: 'Unstructured: Start with a single opening question',
            content: 'Then let conversation flow naturally. Example: "Tell me your story of becoming a researcher" (no follow-up questions prepared).'
          },
          {
            title: 'Unstructured: Use minimal encouragers',
            content: '"Mm-hmm", "I see", "Tell me more" (not questions). Keeps respondent talking without directing content.'
          },
          {
            title: 'Unstructured: Follow respondent\'s lead',
            content: 'They mention "ethics approval struggles" → explore that, even if not in your plan. Prepare themes, not questions.'
          },
          {
            title: 'Unstructured: Tolerate silence',
            content: 'Count to 5 before filling pauses—respondents often add more after silence. Resist urge to rescue awkward silences.'
          }
        ]
      },
      B3: {
        id: 'B3',
        title: 'Probing Techniques',
        introduction: 'Probing elicits deeper, richer responses without leading the respondent. Master these techniques to move beyond surface-level answers.',
        tips: [
          {
            title: 'Silent probe (pause 3-5 seconds)',
            content: 'Most powerful but hardest to execute. After respondent finishes, stay silent—they\'ll often add more detail. Especially effective in unstructured/semi-structured.'
          },
          {
            title: 'Echo probe (repeat last words)',
            content: 'Encourages elaboration. Respondent: "It was challenging." → You: "Challenging?" → Respondent explains why.'
          },
          {
            title: 'Elaboration probe',
            content: '"Can you tell me more about that?" Neutral, non-directive, works in any interview type.'
          },
          {
            title: 'Example probe',
            content: '"Can you give me a specific example?" Moves from abstract ("it was hard") to concrete ("when I tried to code variables...").'
          },
          {
            title: 'Clarification probe',
            content: '"What do you mean by [term they used]?" Ensures you understand their terminology (avoid assumptions).'
          },
          {
            title: 'Contrast probe',
            content: '"How does that compare to [earlier point they made]?" Highlights contradictions or nuances.'
          },
          {
            title: 'Completion probe',
            content: '"And then what happened?" Encourages narrative flow, fills gaps in story.'
          },
          {
            title: 'Emotion probe',
            content: '"How did you feel when that happened?" Captures affective dimensions (feelings, not just facts).'
          },
          {
            title: 'Hypothetical probe',
            content: '"If you had unlimited time/resources, what would you do differently?" Reveals ideals vs. constraints.'
          },
          {
            title: 'Summary probe',
            content: '"Let me make sure I understand... [paraphrase]. Is that right?" Member checking, validates interpretation.'
          }
        ]
      },
      B4: {
        id: 'B4',
        title: 'Question Sequencing & Flow',
        introduction: 'Interview flow affects depth and quality of responses. Strategic sequencing builds trust, maintains engagement, and elicits richer data.',
        tips: [
          {
            title: 'Use funnel sequence',
            content: 'Broad → Narrow (like survey design). Example: "Tell me about your research" → "What methodology?" → "Why that specific technique?"'
          },
          {
            title: 'Start with easy, factual questions',
            content: 'Build confidence before complex/sensitive topics. Opening: "What is your research field?" (easy). Later: "What ethical dilemmas did you face?" (requires trust).'
          },
          {
            title: 'Use inverted funnel for sensitive topics',
            content: 'Specific → General (opposite of normal). Example (income): "Do you earn over $100k?" [Yes] → "What income range?" Easier to answer specific Yes/No first.'
          },
          {
            title: 'Group related questions',
            content: 'All questions on one topic together (like survey sections). Respondent gets into "flow state" on a topic, gives richer answers.'
          },
          {
            title: 'Use transitions explicitly',
            content: '"Now I\'d like to shift to a different topic..." Signals mental break, prepares respondent for new subject.'
          },
          {
            title: 'Place complex questions mid-interview',
            content: 'Not at start (too hard) or end (fatigue). Peak attention: 15-30 minutes into interview.'
          },
          {
            title: 'Save "what else?" for end',
            content: '"Is there anything I didn\'t ask that you think is important?" Captures unexpected insights, signals closing.'
          },
          {
            title: 'Avoid "why" early',
            content: '"Why did you choose that?" feels interrogative at start. Build rapport first with "how" and "what" questions (less threatening).'
          },
          {
            title: 'Space out emotionally heavy questions',
            content: 'If discussing difficult topics, intersperse with lighter questions. Don\'t cluster 5 emotional questions in a row.'
          },
          {
            title: 'End positively',
            content: '"What are you most proud of in your research?" Leaves respondent feeling good (important for rapport, future research relationships).'
          }
        ]
      },
      B5: {
        id: 'B5',
        title: 'Recording & Documentation',
        introduction: 'Proper recording and documentation ensure data quality and ethical compliance. Choose recording method based on context, consent, and analysis needs.',
        tips: [
          {
            title: 'Always get explicit consent for recording',
            content: 'Verbal consent on tape + written consent form. Script: "I\'d like to audio record this interview for accuracy. Is that okay with you?"'
          },
          {
            title: 'Use two recording devices',
            content: 'Primary (phone/recorder) + backup (laptop/second device). Murphy\'s Law: 5-10% of recordings fail (corrupted file, dead battery, user error).'
          },
          {
            title: 'Test equipment before every interview',
            content: 'Record 30 seconds, play back, check volume. Common issues: Microphone off, wrong input device, low battery.'
          },
          {
            title: 'Place recorder strategically',
            content: 'Within 3 feet of respondent, away from noise sources. Avoid: Fans, air conditioning, traffic noise, echoing rooms.'
          },
          {
            title: 'Take field notes even when recording',
            content: 'Capture non-verbal cues, context, your thoughts. Example notes: "[Long pause, looking down]", "Seemed uncomfortable with this question".'
          },
          {
            title: 'Transcribe verbatim for interpretive research',
            content: 'Include: Pauses (...), fillers (um, uh), overlapping speech, emotional cues [laughs]. Non-verbals carry meaning in interpretive research.'
          },
          {
            title: 'Anonymize during transcription',
            content: 'Replace names with pseudonyms, remove identifying details. Example: "[Participant 3]", "[Company name]", "[City in midwest]".'
          },
          {
            title: 'Transcribe within 48-72 hours',
            content: 'Memory fades fast—you\'ll miss context if you wait. Time estimate: 4-6 hours to transcribe 1 hour of audio.'
          },
          {
            title: 'Store securely',
            content: 'Encrypted drives, password protection, secure cloud with institutional approval. Separate: Audio files from transcripts from consent forms.'
          },
          {
            title: 'Consider professional transcription',
            content: 'If budget allows, outsource to save time. Verify: Confidentiality agreement, accuracy check (re-listen to 10-20% of transcripts).'
          }
        ]
      },
      B6: {
        id: 'B6',
        title: 'Building Rapport',
        introduction: 'Rapport = trust + comfort. Strong rapport leads to deeper, more honest responses. Building rapport is a skill that improves with practice.',
        tips: [
          {
            title: 'Start with small talk (3-5 minutes)',
            content: 'Comment on weather, commute, environment. "Thanks for meeting me here—this is a great café!" Don\'t rush into questions immediately (feels interrogative).'
          },
          {
            title: 'Explain purpose clearly',
            content: '"I\'m trying to understand how researchers design surveys. There are no right/wrong answers—I\'m learning from your experience." Reduces performance anxiety.'
          },
          {
            title: 'Emphasize confidentiality explicitly',
            content: '"Nothing you say will be identifiable. I\'ll use pseudonyms in any reports." Especially important for sensitive topics.'
          },
          {
            title: 'Use active listening cues',
            content: 'Nod, maintain eye contact, lean forward slightly. Shows engagement (but don\'t overdo—looks fake).'
          },
          {
            title: 'Mirror respondent\'s energy',
            content: 'If they\'re formal, stay professional; if casual, relax. Match pacing, tone, formality (builds subconscious connection).'
          },
          {
            title: 'Share (appropriate) personal info',
            content: '"I also struggled with methodology design in my research." Humanizes you, levels power dynamic. Warning: Don\'t overshare or dominate.'
          },
          {
            title: 'Validate their experiences',
            content: '"That sounds really challenging" or "I can see why you\'d feel that way." Validation ≠ agreement (you\'re acknowledging, not endorsing).'
          },
          {
            title: 'Avoid judgment language',
            content: 'Don\'t say "That\'s wrong" or "You should have..." Neutral probes: "Interesting. What led to that decision?"'
          },
          {
            title: 'Give control',
            content: '"If any question makes you uncomfortable, just let me know and we can skip it." Empowers respondent, increases trust.'
          },
          {
            title: 'End with gratitude',
            content: '"This was so valuable. Your insights will really help my research." Leaves positive impression (important if you need follow-up interviews).'
          }
        ]
      },
      B7: {
        id: 'B7',
        title: 'Interview Protocol Checklist',
        introduction: 'A protocol ensures consistency across interviews and helps you prepare thoroughly. Use this checklist before, during, and after each interview.',
        tips: [
          {
            title: 'Before: Schedule confirmed',
            content: 'Date, time, location/platform confirmed with participant. Send calendar invite with all details.'
          },
          {
            title: 'Before: Materials ready',
            content: 'Consent form printed/digital (2 copies), recording devices charged & tested, interview guide printed, background on respondent reviewed.'
          },
          {
            title: 'Before: Environment prepared',
            content: 'Quiet location secured, arrive 5-10 minutes early to set up, water available for you and respondent.'
          },
          {
            title: 'During: Opening protocol',
            content: 'Introduce yourself & study (1-2 min), get verbal + written consent, start recording & state date/time, begin with warm-up questions.'
          },
          {
            title: 'During: Interview execution',
            content: 'Follow interview guide (but allow flexibility), take field notes, use probing techniques, monitor time.'
          },
          {
            title: 'During: Closing protocol',
            content: 'Ask "anything else?" at end, thank respondent & explain next steps, stop recording explicitly ("I\'m stopping the recording now").'
          },
          {
            title: 'After: Immediate actions',
            content: 'Save recording immediately (backup to cloud within 1 hour), write post-interview memo (themes, surprises, methodological notes).'
          },
          {
            title: 'After: Within 48 hours',
            content: 'Transcribe or send to transcription service, send thank-you email, provide compensation if promised.'
          },
          {
            title: 'After: Data management',
            content: 'Anonymize transcript (replace names, locations, identifiers), store files securely (encrypted folder, password-protected).'
          },
          {
            title: 'After: Reflection',
            content: 'What went well? What to improve for next interview? Update interview log (track who, when, duration, key themes). Identify follow-up questions.'
          }
        ]
      }
    }
  },
  C: {
    id: 'C',
    title: 'Focus Groups',
    icon: 'UserPlus',
    description: 'Plan and facilitate effective group discussions for rich qualitative insights.',
    steps: {
      C1: {
        id: 'C1',
        title: 'Planning & Logistics',
        introduction: 'Focus groups are structured group discussions (6-10 participants) that generate data through interaction. Success depends on meticulous planning—logistics are 50% of the work.',
        tips: [
          {
            title: 'Ideal size: 6-8 participants',
            content: 'Small enough for everyone to speak, large enough for diverse views. Too few (<5) = feels like interview. Too many (>10) = some participants silent, hard to moderate.'
          },
          {
            title: 'Recruit 10-12 people (expect 20-30% no-shows)',
            content: 'Overbook to account for dropouts. Send reminder emails 48 hours and 2 hours before session.'
          },
          {
            title: 'Duration: 90-120 minutes',
            content: 'Shorter = superficial; Longer = fatigue. Include: 10 min intros + 10 min warm-up + 60 min discussion + 10 min wrap-up.'
          },
          {
            title: 'Choose accessible location',
            content: 'Neutral, comfortable, quiet (not a participant\'s home/office). Options: University seminar room, community center, hotel conference room.'
          },
          {
            title: 'Arrange seating in circle/U-shape',
            content: 'Everyone can see each other, no "head of table". Promotes equal participation (vs. classroom rows or boardroom setup).'
          },
          {
            title: 'Provide refreshments',
            content: 'Coffee, water, light snacks (not full meal—eating disrupts discussion). Shows appreciation, makes participants comfortable.'
          },
          {
            title: 'Offer incentive ($30-50 or equivalent)',
            content: 'Compensates time, increases commitment. Give at end (after discussion) to ensure completion.'
          },
          {
            title: 'Use co-moderator',
            content: 'One leads discussion, one takes notes + observes dynamics. Co-moderator can step in if moderator misses a point or needs backup.'
          },
          {
            title: 'Test technology',
            content: 'Video/audio recording devices, microphones, laptop for notes. Focus groups generate 60-90 min of audio—ensure high-quality recording.'
          },
          {
            title: 'Plan for accessibility',
            content: 'If any participants have disabilities, accommodate in advance. Wheelchair access, sign language interpreter, large-print materials.'
          }
        ]
      },
      C2: {
        id: 'C2',
        title: 'Participant Recruitment & Selection',
        introduction: 'Focus groups work best with homogeneous groups (similar backgrounds/experiences) to facilitate open discussion. Strategic recruitment is key.',
        tips: [
          {
            title: 'Homogeneous groups = better discussion',
            content: 'Participants with shared experiences talk more freely. Example: All PhD students, OR all managers, OR all first-time researchers. Heterogeneous groups = power dynamics, self-censorship.'
          },
          {
            title: 'Segment by key characteristics',
            content: 'If studying different populations, run separate groups. Example: Run 3 groups: One for undergrads, one for Master\'s, one for PhD. Don\'t mix in one group.'
          },
          {
            title: 'Avoid pre-existing power dynamics',
            content: 'Don\'t mix boss + employee, professor + student. People won\'t speak freely in front of authority figures.'
          },
          {
            title: 'Screen for eligibility',
            content: 'Ensure all participants meet criteria (experience level, usage). Screening question: "Have you designed a research survey in the past 12 months?" [Yes] → Invited.'
          },
          {
            title: 'Avoid friends/family in same group',
            content: 'They have side conversations, inside jokes. Recruit strangers to each other (or very weak ties).'
          },
          {
            title: 'Recruit from multiple channels',
            content: 'Email lists, social media, flyers, snowball sampling. Don\'t rely on single source (biases sample).'
          },
          {
            title: 'Explain purpose clearly in invitation',
            content: '"Discuss your experiences with research survey design." Transparency increases attendance and prepares participants mentally.'
          },
          {
            title: 'Offer flexible scheduling',
            content: 'Poll participants for availability before setting date. Tools: Doodle, When2Meet (find time that works for most).'
          },
          {
            title: 'Send detailed logistics email',
            content: 'Address, parking, what to bring, expected duration, incentive. Reduces no-shows (people forget details).'
          },
          {
            title: 'Confirm 24-48 hours before',
            content: '"Looking forward to seeing you tomorrow at 2pm!" Gentle reminder reduces last-minute cancellations.'
          }
        ]
      },
      C3: {
        id: 'C3',
        title: 'Discussion Guide Design',
        introduction: 'A discussion guide (not a script) outlines topics and sample questions. It\'s more flexible than an interview guide, designed to facilitate group interaction.',
        tips: [
          {
            title: 'Prepare 5-7 open-ended questions max',
            content: 'Each question generates 10-15 min discussion. More questions = no time for depth (better to go deep on 5 topics than skim 15).'
          },
          {
            title: 'Use funnel structure',
            content: 'Opening (broad) → Key topics (specific) → Closing (wrap-up). Opening: "What comes to mind when you hear \'survey design\'?" Closing: "If you could give one piece of advice..."'
          },
          {
            title: 'Start with easy warm-up',
            content: 'Round-robin introductions + ice-breaker. "Share your name, research field, and one thing you\'re working on right now" (gets everyone talking early).'
          },
          {
            title: 'Use stimulus materials',
            content: 'Show examples (good/bad survey questions) and ask reactions. "Here\'s a survey question [on screen]. What do you think?" (prompts discussion).'
          },
          {
            title: 'Ask for consensus AND disagreement',
            content: '"Does everyone agree, or do some of you see it differently?" Highlights diversity of perspectives (goal of focus groups).'
          },
          {
            title: 'Include ranking/prioritizing activities',
            content: '"Rank these 5 survey question types from easiest to hardest." Interactive activity breaks up talk, generates energy.'
          },
          {
            title: 'Prepare follow-up probes',
            content: 'Under each main question, list 3-5 probes. Main Q: "What challenges did you face?" Probes: "Can you give an example?", "How did you solve that?"'
          },
          {
            title: 'End with future-oriented question',
            content: '"If you could design the perfect survey tool, what would it include?" Shifts from past/present to aspirational (often generates novel ideas).'
          },
          {
            title: 'Time-box each section',
            content: 'Allocate minutes per topic (visible to you, not participants). Example: Intro (10 min), Topic 1 (20 min), Topic 2 (20 min), Wrap (10 min).'
          },
          {
            title: 'Print on single page if possible',
            content: 'At a glance, you can see where you are in discussion. Use large font, clear headings, highlight key questions.'
          }
        ]
      },
      C4: {
        id: 'C4',
        title: 'Facilitation Techniques',
        introduction: 'Moderating a focus group is an art—balancing control with openness, ensuring everyone speaks, managing conflicts, and keeping discussion on track.',
        tips: [
          {
            title: 'Set ground rules at start',
            content: '"One person at a time, all perspectives valued, no right/wrong answers, phone on silent." Prevents interruptions, creates safe space.'
          },
          {
            title: 'Use open body language',
            content: 'Uncross arms, lean forward, nod, make eye contact with all. Signals you\'re listening and engaged (encourages participation).'
          },
          {
            title: 'Direct questions to specific individuals (gently)',
            content: '"Jordan, you\'ve been quiet—what\'s your take on this?" Brings in silent members without putting them on spot aggressively.'
          },
          {
            title: 'Use round-robin for key questions',
            content: '"Let\'s go around the circle—everyone shares one challenge they faced." Ensures equal airtime (especially at start to establish norm).'
          },
          {
            title: 'Redirect tangents',
            content: '"That\'s interesting, but let\'s get back to [topic]. We can return to this later if time permits." Keeps discussion focused.'
          },
          {
            title: 'Paraphrase and reflect',
            content: '"So what I\'m hearing is [summarize]. Is that accurate?" Shows understanding, gives participants chance to clarify.'
          },
          {
            title: 'Probe for depth, not just breadth',
            content: 'Don\'t jump to next question after first response. Ask: "Why?", "Can you say more?", "Does anyone have a different experience?"'
          },
          {
            title: 'Manage dominant speakers',
            content: '"Thanks, [name]. Let\'s hear from someone who hasn\'t spoken yet." Polite but firm (don\'t let one person monopolize).'
          },
          {
            title: 'Use silence strategically',
            content: 'After asking a question, wait 5-10 seconds before prompting. Someone will fill the silence (often the quieter participants).'
          },
          {
            title: 'Check time discreetly',
            content: 'Glance at clock/watch, but don\'t obsess (kills momentum). If behind, skip lower-priority questions OR extend time if participants willing.'
          }
        ]
      },
      C5: {
        id: 'C5',
        title: 'Managing Group Dynamics',
        introduction: 'Group dynamics = interactions, power relations, conformity pressures. Managing these ensures quality data and ethical treatment of participants.',
        tips: [
          {
            title: 'Watch for groupthink',
            content: 'If everyone quickly agrees, probe: "Does anyone see it differently?" Conformity pressure suppresses dissent (your job to invite alternative views).'
          },
          {
            title: 'Balance extroverts and introverts',
            content: 'Direct questions to quiet members, gently rein in talkers. Extroverts answer fast (surface-level); Introverts answer slow (deeper thought) → value both.'
          },
          {
            title: 'Prevent social desirability bias',
            content: 'Remind: "There are no right answers. I\'m interested in YOUR honest experience." People tend to give "good" answers in groups.'
          },
          {
            title: 'Address power imbalances early',
            content: 'If you notice someone being deferred to (older, more experienced), redirect. "Let\'s hear from some of the newer researchers too."'
          },
          {
            title: 'Use writing exercises',
            content: '"Take 1 minute to jot down your top 3 challenges, then we\'ll share." Prevents dominant speaker from setting tone (everyone generates ideas independently first).'
          },
          {
            title: 'Seat strategically',
            content: 'Place dominant speakers next to moderator (easier to manage), quiet members across (easier to see).'
          },
          {
            title: 'Normalize disagreement',
            content: '"It\'s great to hear different perspectives—that\'s why we have a group." Signals diversity is valued (reduces pressure to conform).'
          },
          {
            title: 'Monitor energy levels',
            content: 'If group is dragging (slouching, yawning), take a 5-min break or do an active exercise. Activity: "Stand up, stretch, grab more coffee" (resets energy).'
          },
          {
            title: 'Use "parking lot" for off-topic ideas',
            content: 'Flip chart labeled "Other Ideas"—write down tangents to revisit later. Validates contribution without derailing discussion.'
          },
          {
            title: 'Watch for non-verbal cues',
            content: 'Eye rolls, crossed arms, checking phone = disengagement or disagreement. Address directly: "I noticed some hesitation—does someone want to add?"'
          }
        ]
      },
      C6: {
        id: 'C6',
        title: 'Recording & Analysis Planning',
        introduction: 'Focus groups generate massive amounts of data (60-90 min of multi-person discussion). Proper recording and analysis planning is essential.',
        tips: [
          {
            title: 'Use video + audio recording',
            content: 'Video captures who said what (critical for transcription). Audio alone = hard to identify speakers (6-8 overlapping voices). Get consent for video separately.'
          },
          {
            title: 'Use multiple microphones',
            content: 'One central mic captures moderator; individual mics capture participants. Budget option: Zoom H6 recorder with 4 external mics.'
          },
          {
            title: 'Assign speaker IDs during transcription',
            content: 'Use pseudonyms or codes (P1, P2, P3...). "P3: I found Likert scales confusing." (Not: "Someone said...")'
          },
          {
            title: 'Transcribe verbatim (including overlaps)',
            content: 'Capture simultaneous speech, interruptions. Example: "P2: I agree [P5: Me too!] that open-ended questions take forever to analyze."'
          },
          {
            title: 'Note non-verbal interactions',
            content: '[P4 nods vigorously], [Group laughs], [Long pause]. These carry meaning in focus group analysis.'
          },
          {
            title: 'Co-moderator takes "real-time notes"',
            content: 'Key quotes, themes, dynamics, surprises. Debrief with moderator immediately after (memory fades fast).'
          },
          {
            title: 'Create speaker map',
            content: 'Diagram of who sat where, with notes on each person. Helps during analysis when you can\'t recall who was who.'
          },
          {
            title: 'Transcribe within 1 week',
            content: 'Longer wait = you forget context, harder to interpret ambiguous parts.'
          },
          {
            title: 'Use software for analysis',
            content: 'NVivo, MAXQDA, or Atlas.ti for thematic coding. Code for: Individual perspectives + Group consensus/dissent + Interaction dynamics.'
          },
          {
            title: 'Analyze across groups, not just within',
            content: 'Run 3-5 focus groups, then compare themes. Saturation = when new groups generate no new themes (usually after 4-6 groups).'
          }
        ]
      }
    }
  },
  D: {
    id: 'D',
    title: 'Secondary/Administrative Data',
    icon: 'Database',
    description: 'Access and evaluate existing datasets for your research with proper ethical considerations.',
    steps: {
      D1: {
        id: 'D1',
        title: 'Identifying & Accessing Secondary Data Sources',
        introduction: 'Secondary data = data collected by someone else for a different purpose. Includes government databases, organizational records, publicly available datasets, archived research data. Cost-effective and time-saving but requires careful evaluation.',
        tips: [
          {
            title: 'Start with official government sources',
            content: 'Census data, national statistics, public health databases. Examples: US Census Bureau, World Bank Open Data, Eurostat, OECD Data Portal. Benefit: High quality, large sample sizes, longitudinal data.'
          },
          {
            title: 'Explore academic data repositories',
            content: 'Re-use datasets from published studies. Sources: ICPSR, UK Data Service, Harvard Dataverse. Benefit: Peer-reviewed quality, codebooks provided, often cleaned data.'
          },
          {
            title: 'Leverage organizational administrative data',
            content: 'Internal records from companies, hospitals, schools. Examples: HR records, patient records (de-identified), student enrollment data. Challenge: Access requires permissions, data governance agreements.'
          },
          {
            title: 'Use data aggregators and portals',
            content: 'One-stop shops for multiple datasets. Examples: Google Dataset Search, Kaggle Datasets, data.gov. Tip: Use filters (date range, file format, license type) to narrow results.'
          },
          {
            title: 'Check for data dictionaries and codebooks',
            content: 'Documentation explaining variables, coding, methodology. Essential for understanding what variables mean, how data was collected. Red flag: No codebook = high risk of misinterpretation.'
          },
          {
            title: 'Verify temporal and geographical alignment',
            content: 'Ensure data matches your research context. Example: Studying 2020-2023 trends? Don\'t use 2015 data. Check data collection dates in metadata.'
          },
          {
            title: 'Assess compatibility with your research question',
            content: 'Secondary data rarely perfectly fits your needs. Ask: Does this dataset have the variables I need? The population I\'m studying? The timeframe?'
          },
          {
            title: 'Understand access restrictions and licensing',
            content: 'Not all "public" data is freely usable. Levels: Open → Registered access → Restricted → Confidential. Check license for sharing, combining, publishing.'
          },
          {
            title: 'Plan for data cleaning and recoding',
            content: 'Secondary data is rarely analysis-ready. Common issues: Missing values coded inconsistently, variables need transformation. Budget time: 30-50% of analysis time is data cleaning.'
          },
          {
            title: 'Document provenance and cite properly',
            content: 'Track where data came from and give credit. Record: Source name, date accessed, version number, DOI or URL, citation format.'
          }
        ]
      },
      D2: {
        id: 'D2',
        title: 'Evaluating Data Quality & Credibility',
        introduction: 'Not all secondary data is created equal. Quality varies widely—from rigorously collected government surveys to unreliable scraped web data. Systematic evaluation prevents "garbage in, garbage out" research.',
        tips: [
          {
            title: 'Apply the CRAAP test',
            content: 'Currency, Relevance, Authority, Accuracy, Purpose. Currency: How old? Relevance: Right population? Authority: Reputable organization? Accuracy: Methodology documented? Purpose: Objective or biased?'
          },
          {
            title: 'Check sample representativeness',
            content: 'Does the sample reflect the population of interest? Red flags: Convenience sample, low response rate (<30%), biased sampling. Gold standard: Probability sample, high response rate (>60%).'
          },
          {
            title: 'Assess measurement validity',
            content: 'Do the measures capture what they claim to measure? Check: Are variables defined clearly? Are scales validated? Warning: Proxy measures may not align with your construct.'
          },
          {
            title: 'Examine data completeness',
            content: 'How much missing data is there? Rule of thumb: <5% = minimal concern; 5-20% = manageable; >20% = major issue. Check patterns of missingness.'
          },
          {
            title: 'Verify data collection methodology',
            content: 'How was the data gathered? Surveys: Mode, question wording, response options. Administrative data: Data entry process, quality control checks. Red flag: No methodology documentation.'
          },
          {
            title: 'Check for known biases or limitations',
            content: 'Read critiques, published studies using the data. Sources of bias: Social desirability, recall bias, selection bias. Tip: Search "[dataset name] limitations".'
          },
          {
            title: 'Compare with other data sources',
            content: 'Triangulate to validate findings. If multiple datasets show same trend, more confident in results. If conflicting, investigate why.'
          },
          {
            title: 'Assess temporal consistency',
            content: 'Has data collection remained consistent over time? Important for longitudinal analysis. Check for "break in series" warnings.'
          },
          {
            title: 'Evaluate dataset documentation quality',
            content: 'Is there sufficient metadata? Minimum: Variable names/definitions, coding schemes, sample description, collection dates. Bonus: Survey instrument, sampling weights.'
          },
          {
            title: 'Test data integrity with descriptive statistics',
            content: 'Run basic checks before full analysis. Check: Value ranges, frequencies, cross-tabs. Red flags: Impossible values, extreme outliers, inconsistencies.'
          }
        ]
      },
      D3: {
        id: 'D3',
        title: 'Ethical & Legal Considerations',
        introduction: 'Using secondary data doesn\'t exempt you from ethical responsibilities. Issues include informed consent (was it obtained?), privacy (can individuals be re-identified?), and legal restrictions (data sharing agreements, licensing).',
        tips: [
          {
            title: 'Determine if ethics approval is needed',
            content: 'Even for public data, institutional review may be required. Need approval if: Data contains identifiable information, combining datasets could enable re-identification, studying vulnerable populations.'
          },
          {
            title: 'Verify original consent scope',
            content: 'Did participants consent to secondary use? Best case: Original consent included "data may be used for future research." Problem: Consent explicitly limited to original study (you CANNOT use).'
          },
          {
            title: 'Assess re-identification risk',
            content: 'Can individuals be identified from the data? High risk: Dates of birth, ZIP codes, rare diagnoses. Low risk: Aggregated data, broad categories. Best practice: k-anonymity.'
          },
          {
            title: 'Respect data use agreements (DUAs)',
            content: 'Read and follow terms carefully. Common restrictions: Cannot share, must destroy after X years, cannot merge with other datasets, cannot attempt re-identification.'
          },
          {
            title: 'Protect sensitive data appropriately',
            content: 'Store securely, limit access. Minimum: Password-protected files, encrypted storage. For restricted data: Locked storage, access logs, approved personnel only.'
          },
          {
            title: 'Anonymize data before sharing or publishing',
            content: 'Remove identifiers from any shared outputs. Direct identifiers: Names, addresses, phone numbers. Indirect identifiers: Combinations that could re-identify.'
          },
          {
            title: 'Cite data sources and acknowledge creators',
            content: 'Give credit ethically and legally. Include: Dataset name, version, date accessed, DOI or URL, original creators. Some datasets require specific citation format.'
          },
          {
            title: 'Avoid misrepresenting data or findings',
            content: 'Use data within its limitations. Don\'t: Claim causation from correlational data, generalize beyond sample scope, cherry-pick results. Do: Report limitations, acknowledge uncertainty.'
          },
          {
            title: 'Check for Indigenous data sovereignty issues',
            content: 'Special considerations for Indigenous communities. Principles: OCAP (Ownership, Control, Access, Possession). Before using: Consult with community leaders, seek permission.'
          },
          {
            title: 'Be transparent about data provenance in publications',
            content: 'Readers need to assess credibility. Report: Where data came from, who collected it, when, how, sample characteristics, known limitations. Include data availability statement.'
          }
        ]
      }
    }
  },
  E: {
    id: 'E',
    title: 'Observation Methods',
    icon: 'Eye',
    description: 'Conduct structured and participant observation with proper documentation techniques.',
    steps: {
      E1: {
        id: 'E1',
        title: 'Structured Observation - Planning & Design',
        introduction: 'Structured observation = systematic recording of behaviors using predefined categories and time intervals. Used when you know what to look for. Common in psychology, education, healthcare, organizational research.',
        tips: [
          {
            title: 'Define observable behaviors precisely',
            content: 'Operationalize abstract concepts into concrete actions. Vague: "Observe student engagement". Precise: "Count: (1) raising hand, (2) asking questions, (3) taking notes, (4) looking at teacher vs. phone".'
          },
          {
            title: 'Develop a structured observation protocol/checklist',
            content: 'Pre-coded sheet to record behaviors. Include: List of behaviors, frequency count boxes, time intervals, setting details. Tools: Paper forms, tablets with apps, video coding software.'
          },
          {
            title: 'Choose time sampling method',
            content: 'How to slice observation into chunks. Time sampling: Observe for fixed intervals. Event sampling: Record every occurrence of target behavior. Instantaneous sampling: Record at specific moments.'
          },
          {
            title: 'Pilot test the observation scheme',
            content: 'Trial run with 2-3 sessions. Test: Can you reliably code all categories? Are definitions clear? Is timing manageable? Inter-rater reliability: Two observers code same session, calculate agreement.'
          },
          {
            title: 'Minimize observer effects (reactivity)',
            content: 'Your presence changes behavior. Strategies: Observe from behind one-way mirror, use video recording, dress to blend in. Habituation period: First 10-15 minutes, don\'t code.'
          },
          {
            title: 'Plan for observer training',
            content: 'Ensure consistency across observers. Training includes: Practice coding with sample videos, discuss discrepancies, refine definitions until agreement >80%. Ongoing checks: Re-code 10-20% periodically.'
          },
          {
            title: 'Ethical considerations',
            content: 'Informed consent and privacy. Public spaces: Generally allowed without consent (but ethical to inform). Private spaces: Require informed consent. Video recording: Always get consent.'
          }
        ]
      },
      E2: {
        id: 'E2',
        title: 'Structured Observation - Recording & Analysis',
        introduction: 'Recording methods range from paper checklists to video coding software. Analysis focuses on frequencies, patterns, and statistical testing.',
        tips: [
          {
            title: 'Use multiple recording methods',
            content: 'Paper backup + digital/video. Primary: Tablet with observation app (real-time coding, timestamps). Backup: Paper checklist (in case of tech failure). Optional: Video (allows re-coding).'
          },
          {
            title: 'Code in real-time when possible',
            content: 'Immediate coding reduces memory bias. Real-time: Observer marks checklist as behaviors occur (more accurate). Video: Best of both—can pause, rewind (but time-intensive).'
          },
          {
            title: 'Calculate inter-rater reliability',
            content: 'Essential for credibility. Method: 2+ observers independently code same session. Metrics: Percent agreement, Cohen\'s Kappa (>0.7 good, >0.8 excellent). If low: Retrain observers, clarify definitions.'
          },
          {
            title: 'Analyze frequency and duration data',
            content: 'Quantitative analysis of behaviors. Frequency: Count occurrences. Duration: Time spent in each category. Sequence: Analyze patterns. Tools: Excel, SPSS/R, specialized software.'
          },
          {
            title: 'Use lag sequential analysis',
            content: 'Analyze behavioral sequences. Question: Does behavior A predict behavior B? Example: "When teacher asks open-ended question, students are 3x more likely to ask follow-up questions."'
          },
          {
            title: 'Compare across contexts or time',
            content: 'Look for patterns. Between-group: Compare different conditions. Within-subject: Compare same setting at different times. Statistical tests: Chi-square, t-test, ANOVA.'
          },
          {
            title: 'Report inter-observer agreement in publications',
            content: 'Transparency requirement. Report: "Two observers independently coded 25% of observations. Inter-rater reliability was Kappa = 0.82, indicating excellent agreement."'
          }
        ]
      },
      E3: {
        id: 'E3',
        title: 'Participant Observation - Entry & Role Negotiation',
        introduction: 'Participant observation = immersive method where researcher joins the setting, participates in activities, and observes from inside. Used in ethnography, anthropology, qualitative sociology.',
        tips: [
          {
            title: 'Identify and contact gatekeepers',
            content: 'People who control access to the setting. Examples: School principal, CEO, community leader. Approach: Formal letter explaining research purpose, what you\'ll do, time commitment, benefits to them.'
          },
          {
            title: 'Choose your participant-observer role',
            content: 'Spectrum from full participant to observer. Complete participant: Fully immersed, identity hidden. Participant-as-observer: Participate but identity known. Observer-as-participant: Primarily observe. Complete observer: No participation.'
          },
          {
            title: 'Negotiate informed consent transparently',
            content: 'Explain your research without revealing all details. Full disclosure: "I\'m studying workplace collaboration" (general). Partial disclosure: Don\'t reveal specific hypotheses. Ongoing consent: Check in periodically.'
          },
          {
            title: 'Plan your entry strategy',
            content: 'First impressions matter. Timing: Enter during non-critical period. Dress: Match setting norms. Introduction: Brief, clear, humble ("I\'m here to learn from you"). Initial activity: Start with peripheral participation.'
          }
        ]
      },
      E4: {
        id: 'E4',
        title: 'Participant Observation - Fieldwork Techniques',
        introduction: 'Once inside the setting, effective participant observation requires balancing participation with observation, taking detailed field notes, asking good questions, and adapting to evolving understanding.',
        tips: [
          {
            title: 'Master the art of "attentive participation"',
            content: 'Engage meaningfully while observing. Participate enough: Build trust, gain insider perspective. Observe enough: Maintain analytical distance. Balance: Shift between roles flexibly.'
          },
          {
            title: 'Use progressive focusing',
            content: 'Start broad, narrow over time. Phase 1: Descriptive observation—note everything. Phase 2: Focused observation—identify patterns, emerging themes. Phase 3: Selective observation—focus on specific phenomena.'
          },
          {
            title: 'Adopt a stance of "disciplined subjectivity"',
            content: 'Use your reactions as data, but critically. Acknowledge: You\'re not a neutral fly on the wall. Reflect: What emotions did you feel? Why? What biases might you have? Use: Your confusion, surprise are data.'
          },
          {
            title: 'Ask ethnographic questions strategically',
            content: 'Not formal interviews, but casual inquiries. Descriptive: "Can you walk me through how you did that?" Structural: "What are the different types of [category]?" Contrast: "How is X different from Y?"'
          }
        ]
      },
      E5: {
        id: 'E5',
        title: 'Participant Observation - Relationship Management',
        introduction: 'Long-term fieldwork requires managing complex relationships—building trust, navigating conflicts, handling sensitive information, maintaining boundaries, and managing emotional labor.',
        tips: [
          {
            title: 'Cultivate key informants strategically',
            content: 'Build relationships with insiders who can explain culture. Characteristics: Articulate, reflective, respected by group, willing to share. Multiple informants: Don\'t rely on one. Reciprocity: Give back.'
          },
          {
            title: 'Navigate dual role tensions',
            content: 'You\'re both insider and outsider, friend and researcher. Insider/outsider: Participants may forget you\'re a researcher. Strategy: Be transparent about boundaries. Ethical dilemma: If you witness unethical behavior, discuss with ethics board.'
          },
          {
            title: 'Manage reactivity over time',
            content: 'Your presence affects the setting (unavoidable, but minimize). Early: High reactivity (people perform). Mid: Habituation (people forget you\'re there)—best data emerges. Late: Over-familiarity—periodically take outsider view.'
          },
          {
            title: 'Handle emotional labor',
            content: 'Fieldwork is emotionally taxing. Challenges: Witnessing suffering, managing friendships that end. Self-care: Debrief with supervisor/peers, journal reflections, set boundaries, seek counseling if needed.'
          }
        ]
      },
      E6: {
        id: 'E6',
        title: 'Participant Observation - Exit & Reflection',
        introduction: 'Exiting the field is a process, not an event. Requires saying goodbye ethically, sharing findings, reflecting on what you learned, and analyzing field notes systematically.',
        tips: [
          {
            title: 'Plan a gradual exit',
            content: 'Don\'t disappear suddenly. Timeline: Announce exit 2-4 weeks in advance. Taper: Reduce presence gradually. Final rituals: Attend goodbye event, write thank-you notes, bring small gifts if appropriate.'
          },
          {
            title: 'Member checking: Share and validate findings',
            content: 'Ethical obligation and data quality check. Method: Present preliminary findings to participants. Purpose: Verify interpretations, catch errors, give voice to participants. Manage disagreement: Investigate why.'
          },
          {
            title: 'Reflect on your role and biases',
            content: 'Ethnographic reflexivity. Questions: How did my identity shape access? What assumptions did I bring? What surprised me? Document: Write reflexive memo capturing your positionality.'
          },
          {
            title: 'Conduct systematic analysis of field notes',
            content: 'From notes to themes to theory. Steps: Read all notes chronologically → Open coding → Axial coding (connect themes) → Selective coding (develop core narrative) → Write thick description.'
          }
        ]
      }
    }
  },
  F: {
    id: 'F',
    title: 'Textual & Visual Data',
    icon: 'FileText',
    description: 'Analyze documents, field notes, and visual materials with systematic coding techniques.',
    steps: {
      F1: {
        id: 'F1',
        title: 'Document Analysis - Selection & Sampling',
        introduction: 'Document analysis = systematic examination of written, printed, or digital texts. Documents provide context, triangulation, and access to phenomena you can\'t observe directly.',
        tips: [
          {
            title: 'Define your document universe',
            content: 'What counts as relevant? Boundaries: Time period, type (official vs. informal), source (internal vs. public), medium (print vs. digital). Challenge: Digital abundance—need sampling strategy.'
          },
          {
            title: 'Distinguish document types and purposes',
            content: 'Each type has different credibility and bias. Official documents: High credibility, may be sanitized. Personal documents: Rich insights, subjective. Mass media: Public discourse, agenda-driven.'
          },
          {
            title: 'Use purposive or theoretical sampling',
            content: 'Not random—select documents strategically. Criterion sampling: All documents meeting specific criteria. Maximum variation: Documents representing diverse perspectives. Snowball: Follow citation trail.'
          },
          {
            title: 'Assess document authenticity and credibility',
            content: 'Is it what it claims to be? Authenticity: Is the author who they claim? Credibility: Is content accurate? Representativeness: Typical or outlier? Meaning: What did it mean to original audience?'
          },
          {
            title: 'Access private or restricted documents ethically',
            content: 'Permission and confidentiality. Public documents: No permission needed, but cite properly. Organizational: Require permission, sign DUA. Personal: Require informed consent from author.'
          },
          {
            title: 'Plan for volume and manageability',
            content: 'Documents accumulate fast. Rule of thumb: Can you reasonably read and code all? If not, sample further. Tools for large corpora: Text mining, NVivo/MAXQDA, Python/R.'
          },
          {
            title: 'Document your sampling decisions',
            content: 'Transparency for readers. Report: Inclusion/exclusion criteria, search terms used, date range, number reviewed vs. included, rationale for sampling.'
          }
        ]
      },
      F2: {
        id: 'F2',
        title: 'Document Analysis - Critical Reading & Coding',
        introduction: 'Analyzing documents requires reading between the lines—what\'s said, what\'s unsaid, how it\'s framed, for whom, and why. Goes beyond content to examine discourse, rhetoric, and power.',
        tips: [
          {
            title: 'Read documents at multiple levels',
            content: 'Surface and deep readings. Literal level: What does it explicitly state? (content analysis). Interpretive level: What assumptions underlie the text? (discourse analysis). Critical level: Whose interests are served? (critical analysis).'
          },
          {
            title: 'Apply appropriate analytical framework',
            content: 'Match method to research question. Content analysis: Count frequencies. Thematic analysis: Identify recurring themes. Discourse analysis: Examine language, power, ideology. Narrative analysis: Structure of stories.'
          },
          {
            title: 'Code systematically using qualitative software',
            content: 'Organization is key. Software: NVivo, MAXQDA, Atlas.ti, Dedoose. Process: Import → Read and code → Organize codes into themes → Query patterns.'
          },
          {
            title: 'Look for what\'s absent',
            content: 'Silences are data. Missing topics: What issues are never mentioned? Missing voices: Whose perspective is excluded? Euphemisms: What\'s implied but not stated?'
          },
          {
            title: 'Triangulate with other data sources',
            content: 'Documents alone can mislead. Compare: What documents say vs. what interviews reveal vs. what observations show. Convergence = more credible; Divergence = interesting finding.'
          },
          {
            title: 'Contextualize historically and culturally',
            content: 'Meaning depends on context. Historical: Document from different era needs contextual interpretation. Cultural: May not translate across contexts. Situational: Context of creation matters.'
          },
          {
            title: 'Write analytic memos throughout',
            content: 'Capture insights as they emerge. When to memo: After coding, when you notice patterns, when puzzled. Content: Emerging themes, connections, questions for exploration.'
          }
        ]
      },
      F3: {
        id: 'F3',
        title: 'Field Notes & Journals - Recording Techniques',
        introduction: 'Field notes = written records of observations, conversations, reflections created during or immediately after fieldwork. Quality of notes directly impacts quality of analysis.',
        tips: [
          {
            title: 'Write field notes ASAP after observation',
            content: 'Memory fades fast. Golden rule: Within 24 hours. Best practice: Write "jottings" during observation (brief keywords), expand into full notes within 2-4 hours.'
          },
          {
            title: 'Use a consistent structure',
            content: 'Makes notes easier to search and analyze. Header: Date, time, location, participants. Body: Descriptive observations, verbatim quotes, sequence. Footer: Reflections, questions, emerging themes.'
          },
          {
            title: 'Balance thick description with focused observation',
            content: 'Rich detail, but not everything. Thick description (Geertz): Rich account that captures context, meaning, interpretation. Focus: Actions, interactions, emotions, meanings.'
          },
          {
            title: 'Distinguish observation levels clearly',
            content: 'Facts vs. interpretations. Level 1: Concrete description (observable). Level 2: Your interpretation (analysis). Level 3: Reflection (your reactions, biases). Use brackets or separate sections.'
          },
          {
            title: 'Capture verbatim quotes when possible',
            content: 'Participants\' own words are invaluable. Mark clearly: Quotation marks for exact words, paraphrase (no quotes) for approximate. If you miss exact words, write "something like".'
          },
          {
            title: 'Include sensory and emotional details',
            content: 'Set the scene. Senses: What did you see, hear, smell, feel? Emotions: Your feelings AND participants\' (observed or inferred).'
          },
          {
            title: 'Note negative cases and surprises',
            content: 'Don\'t ignore data that doesn\'t fit. Negative cases: Instances that contradict emerging patterns (crucial for theory building). Surprises: Signal you don\'t fully understand yet.'
          },
          {
            title: 'Keep a running list of questions and puzzles',
            content: 'Fuel ongoing inquiry. Mark questions throughout notes. Review weekly: Which can be answered through observation? Which need follow-up interviews?'
          }
        ]
      },
      F4: {
        id: 'F4',
        title: 'Field Notes & Journals - Reflexivity & Analysis',
        introduction: 'Reflexivity = critical self-awareness about how your positionality, biases, and presence shape data collection and interpretation. Reflexive journals complement field notes.',
        tips: [
          {
            title: 'Keep a separate reflexive journal',
            content: 'Distinct from descriptive field notes. Field notes: What happened in the setting (external). Reflexive journal: What happened in you (internal). Format: Weekly entries, or after significant events.'
          },
          {
            title: 'Examine your positionality explicitly',
            content: 'Who you are shapes what you see. Dimensions: Race, gender, class, age, education, insider/outsider status. Impact: Some participants may trust you more/less, share/withhold information.'
          },
          {
            title: 'Track evolving interpretations',
            content: 'Your understanding shifts over time. Method: Date all journal entries, re-read periodically, note changes. Shows: Analytic rigor, development of theoretical sensitivity.'
          },
          {
            title: 'Document ethical dilemmas and decisions',
            content: 'How did you navigate tricky situations? Record: What happened, options considered, decision made, rationale, how you feel about it in retrospect.'
          },
          {
            title: 'Write theoretical memos',
            content: 'Bridge field notes to theory. Trigger: When you notice patterns, contradictions, surprises. Content: Describe pattern, give examples, link to theory, pose questions for exploration.'
          },
          {
            title: 'Conduct periodic self-audits',
            content: 'Check your biases regularly. Questions: Am I focusing more on certain participants? Avoiding certain topics? Seeing what I expected? Rushing to conclusions?'
          },
          {
            title: 'Debrief with peers or supervisor',
            content: 'External perspective catches blind spots. Format: Regular meetings, share field notes and journal entries. Benefits: Peer questions reveal assumptions, open new interpretations.'
          },
          {
            title: 'Write "confessional tales" (Van Maanen)',
            content: 'Honest accounts of fieldwork challenges. Purpose: Humanize research process, show methodological transparency, help future researchers. Include in: Methodology appendix, reflexive statement.'
          }
        ]
      },
      F5: {
        id: 'F5',
        title: 'Visual Methods - Design & Implementation',
        introduction: 'Visual methods = using images, diagrams, videos, or asking participants to create visuals as data. Includes photo elicitation, participant-generated drawings, video diaries, social media image analysis.',
        tips: [
          {
            title: 'Choose visual method aligned with research question',
            content: 'Different methods serve different purposes. Photo elicitation: Interview using photos as prompts. Participant-generated photos: Insider perspective. Drawings/diagrams: Access tacit knowledge. Video diaries: Capture change.'
          },
          {
            title: 'Provide clear instructions to participants',
            content: 'Avoid confusion. Specify: What to photograph/draw, how many, timeframe, format, ethical guidelines (no identifiable people without consent). Provide: Sample images, camera/phone if needed.'
          },
          {
            title: 'Obtain informed consent for visual data',
            content: 'Extra ethical layer beyond verbal/textual data. For participants: Consent to be photographed/filmed, consent for images to be used. For third parties: If participants photograph others, they must get consent.'
          },
          {
            title: 'Use photo elicitation interviews',
            content: 'Photos as conversation starters. Method: Show participant photos, ask: "Tell me about this photo. What does it show? What does it mean to you?" Benefits: Concrete starting point, evokes memories and emotions.'
          },
          {
            title: 'Analyze visual data systematically',
            content: 'Not just illustrative—it\'s data. Content analysis: What\'s depicted (objects, people, settings). Compositional analysis: How it\'s depicted (framing, color, perspective). Semiotic analysis: What it means (symbols, connotations).'
          },
          {
            title: 'Consider power and ethics in visual research',
            content: 'Images carry ethical weight. Who creates: Researcher vs. participant (power dynamics differ). Who\'s depicted: Vulnerable populations need extra protection. How used: Publications, presentations require consent.'
          },
          {
            title: 'Store and share visual data securely',
            content: 'Images are often more identifiable than text. Storage: Encrypted, password-protected. Sharing: Only with consent, often need anonymization (blur faces, crop details).'
          },
          {
            title: 'Integrate visuals with other data sources',
            content: 'Triangulation. Combine: Photos + interviews (what does this image mean?). Documents + images (do official photos match reality?). Observations + participant photos (different perspectives on same setting).'
          },
          {
            title: 'Report visual findings appropriately',
            content: 'Don\'t just illustrate—analyze. Use in publications: Selection must be justified (not just pretty pictures). Caption: Describe and analyze. Anonymize: Blur faces, crop identifying details.'
          },
          {
            title: 'Consider creative dissemination',
            content: 'Visuals offer alternative outputs. Options: Photo exhibitions, documentary films, graphic novels, visual reports for non-academic audiences. Benefits: Accessible, engaging, can reach participants and communities.'
          }
        ]
      }
    }
  }
};

export const getPathwayById = (pathwayId) => {
  return dataCollectionPathways[pathwayId] || null;
};

export const getStepById = (pathwayId, stepId) => {
  const pathway = dataCollectionPathways[pathwayId];
  if (!pathway) return null;
  return pathway.steps[stepId] || null;
};

export const getAllPathways = () => {
  return Object.values(dataCollectionPathways);
};

export const getStepsForPathway = (pathwayId) => {
  const pathway = dataCollectionPathways[pathwayId];
  if (!pathway) return [];
  return Object.values(pathway.steps);
};
